'use client';

// Polls the shared /stocks/quotes bulk endpoint for a set of symbols (~15s) and
// returns a map of UPPERCASE symbol → latest quote. The backend coalesces every
// client + symbol into ~1 provider call per 20s, so polling here is cheap — this
// hook just keeps the terminal's price header current between the slower bar
// polls. Last quotes are retained across transient symbol-set churn; quotes for
// symbols that leave the board are pruned.

import { useEffect, useState } from 'react';
import { getLiveQuotes, type LiveQuote } from '@/lib/api-client';

export const POLL_MS = 15_000;

export function useLiveQuotes(symbols: string[], pollMs: number = POLL_MS): Record<string, LiveQuote> {
  const [quotes, setQuotes] = useState<Record<string, LiveQuote>>({});
  // Stable key so the poll only re-subscribes when the symbol SET changes.
  const symsKey = [...new Set(symbols.map((s) => s.toUpperCase()).filter(Boolean))].sort().join(',');
  // Clamp to a sane floor so a bad interval can't hammer the endpoint; changing
  // it re-subscribes at the new cadence.
  const effMs = Number.isFinite(pollMs) && pollMs > 0 ? Math.max(3_000, pollMs) : POLL_MS;

  useEffect(() => {
    const syms = symsKey ? symsKey.split(',') : [];
    if (!syms.length) {
      setQuotes({});
      return;
    }
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | undefined;

    const tick = async () => {
      // Skip while the tab is hidden; the visibility listener fires a catch-up.
      if (!(typeof document !== 'undefined' && document.hidden)) {
        try {
          const list = await getLiveQuotes(syms);
          if (cancelled) return;
          if (list.length) {
            setQuotes((prev) => {
              const next = { ...prev };
              for (const q of list) next[q.symbol.toUpperCase()] = q;
              return next;
            });
          }
        } catch {
          // Keep the last quotes on a failed poll.
        }
      }
      if (!cancelled) timer = setTimeout(tick, effMs);
    };
    // run() restarts the single poll loop (clearing any pending timer) so a
    // visibility catch-up never spawns a second concurrent loop.
    const run = () => {
      if (timer) clearTimeout(timer);
      void tick();
    };

    run();
    const onVis = () => {
      if (typeof document !== 'undefined' && !document.hidden) run();
    };
    document.addEventListener('visibilitychange', onVis);
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
      document.removeEventListener('visibilitychange', onVis);
    };
  }, [symsKey, effMs]);

  // Prune quotes for symbols no longer requested so stale prices don't linger.
  useEffect(() => {
    const want = new Set(symsKey ? symsKey.split(',') : []);
    setQuotes((prev) => {
      let changed = false;
      const next: Record<string, LiveQuote> = {};
      for (const k of Object.keys(prev)) {
        if (want.has(k)) next[k] = prev[k];
        else changed = true;
      }
      return changed ? next : prev;
    });
  }, [symsKey]);

  return quotes;
}
